/** Small helpers both pages share: calling Better Auth and telling the person what happened. */
import { setLoading } from "./loading";

export const $ = <T extends HTMLElement = HTMLElement>(id: string) =>
  document.getElementById(id) as T | null;

export function say(message: string) {
  const error = $("error");
  if (error) error.textContent = "";
  const status = $("status");
  if (status) status.textContent = message;
}

export function fail(message: string) {
  const status = $("status");
  if (status) status.textContent = "";
  const error = $("error");
  if (error) error.textContent = message;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

/**
 * The signed authorize request an app sent the person here with, if any. Posting it with a
 * sign-in or consent call is what lets Better Auth carry on to the app afterwards.
 */
export function oauthQuery(): string | undefined {
  const query = location.search.slice(1);
  return new URLSearchParams(query).has("sig") ? query : undefined;
}

export async function api<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  const query = oauthQuery();
  const res = await fetch(`/api/auth${path}`, {
    method: body ? "POST" : "GET",
    headers: body ? { "content-type": "application/json" } : {},
    body: body ? JSON.stringify(query ? { ...body, oauth_query: query } : body) : null,
    credentials: "same-origin",
  });
  const data = (await res.json().catch(() => null)) as
    | (T & { message?: string; code?: string })
    | null;
  if (!res.ok) throw new ApiError(res.status, data?.code, data?.message ?? "Something went wrong.");
  return data as T;
}

/** After signing in: back to the app if one sent the person here, otherwise to their account. */
export function continueTo(result: { url?: string } | null) {
  location.assign(result?.url ?? "/");
}

/**
 * What a button says once the request is done and the browser is on its way (#32): back to the
 * app that sent the person here (named by the page's `data-app-name`), on to another of our
 * pages, or to their account.
 */
export function leavingLabel(result: { url?: string } | null) {
  if (!result?.url) return "Taking you to your account…";
  if (new URL(result.url, location.href).origin === location.origin) return "Continuing…";
  const app = document.querySelector<HTMLElement>("[data-app-name]")?.dataset.appName;
  return app ? `Taking you back to ${app}…` : "Taking you back…";
}

/**
 * Busy while a request that doesn't leave the page runs (sending a code, adding a passkey), so a
 * double click can't send two; it resets when the request is done.
 */
export async function busy<T>(
  control: HTMLButtonElement | null | undefined,
  run: () => Promise<T>,
) {
  setLoading(control, true);
  try {
    return await run();
  } finally {
    setLoading(control, false);
  }
}
